import React, { useEffect, useRef, useState } from 'react'
import { $isChangeStatusSuccess, changeStatusSubmit } from './model'
import { CButton, CCardBody, CCol, CRow } from '@coreui/react-pro'
import { useTicketsList } from './hooks'
import {
  Button,
  Input,
  InputRef,
  notification,
  Space,
  Table,
  TableColumnsType,
  TableColumnType,
} from 'antd'
import { Link } from 'react-router-dom'
import { path } from '../../../../libs'
import { Ticket } from './types'
import { AddTicketsModal } from '../widgets/addTicketsModal/component'
import { useUnit } from 'effector-react'
import {
  $isAddTicketsSuccess,
  setIsAddTicketsSuccess,
} from '../widgets/addTicketsModal/model'
import { NotificationPlacement } from 'antd/es/notification/interface'
import { Guard } from '../../../../features/session/guard'
import type { FilterDropdownProps } from 'antd/es/table/interface'
import { SearchOutlined } from '@ant-design/icons'
import Highlighter from 'react-highlight-words'

interface DataType {
  userID: number
  ticketID: number
  isUsed: boolean
}
type DataIndex = keyof DataType

export const TicketsList: React.FC = () => {
  const isChangeStatusSuccess = useUnit($isChangeStatusSuccess)

  const { tickets, onChangeTicketsList, totalCountOfTickets, loading } =
    useTicketsList()
  const isAddTicketsSuccess = useUnit($isAddTicketsSuccess)

  const [isAddTicketsModalOpen, setIsAddTicketsModalOpen] = useState(false)

  const [api, contextHolder] = notification.useNotification()

  const openNotification = (placement: NotificationPlacement) => {
    api.success({
      message: `Success`,
      description: `Ticket successfully issued`,
      placement,
    })
  }

  useEffect(() => {
    onChangeTicketsList({})
  }, [])

  useEffect(() => {
    if (isAddTicketsSuccess) {
      onChangeTicketsList({})
    }
  }, [isAddTicketsSuccess])

  useEffect(() => {
    if (isChangeStatusSuccess) {
      openNotification('topRight')
    }
  }, [isChangeStatusSuccess])

  const [searchText, setSearchText] = useState('')
  const [searchedColumn, setSearchedColumn] = useState('')
  const searchInput = useRef<InputRef>(null)

  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps['confirm'],
    dataIndex: DataIndex,
  ) => {
    confirm()
    setSearchText(selectedKeys[0])
    setSearchedColumn(dataIndex)
  }

  const handleReset = (clearFilters: () => void) => {
    clearFilters()
    setSearchText('')
  }

  const getColumnSearchProps = (
    dataIndex: DataIndex,
  ): TableColumnType<DataType> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({ closeDropdown: false })
              setSearchText((selectedKeys as string[])[0])
              setSearchedColumn(dataIndex)
            }}
          >
            Filter
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close()
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  })

  const columns: TableColumnsType<Ticket> = [
    {
      title: 'Ticket',
      dataIndex: 'ticketID',
      key: 'ticketID',
      sorter: (a, b) => a.ticketID - b.ticketID,
      ...getColumnSearchProps('ticketID'),
    },
    {
      title: 'User ID',
      dataIndex: 'userID',
      key: 'userID',
      sorter: (a, b) => a.userID - b.userID,
      ...getColumnSearchProps('userID'),
      render: (id) => (
        <Link
          to={path.clients.details(id)}
          style={{ textDecoration: 'none' }}
          target="_blank"
        >
          {id}
        </Link>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isUsed',
      filters: [
        {
          text: 'Reserved',
          value: false,
        },
        {
          text: 'Issues',
          value: true,
        },
      ],
      onFilter: (value, record) => record.isUsed == value,
      render: (text, record) => {
        if (record.isUsed) {
          return 'Issued'
        }
        return 'Reserved'
      },
    },
    {
      title: 'Actions',
      fixed: 'right',
      width: 150,
      render: (item: Ticket) => {
        return (
          !item.isUsed && (
            <Guard permission="new_year_2026_ban">
              <div className="d-flex flex-column gap-1">
                <CButton
                  size="sm"
                  color="danger"
                  onClick={() => {
                    if (window.confirm('Are you sure')) {
                      changeStatusSubmit({ userID: item.userID })
                    }
                  }}
                >
                  Issue a ticket
                </CButton>
              </div>
            </Guard>
          )
        )
      },
    },
  ]

  return (
    <CCardBody>
      {contextHolder}
      <Guard permission="reserve_ticket_ny26">
        <CRow className="justify-content-start">
          <CCol xs={4} className="d-flex gap-2 mb-2">
            <CButton
              color="primary"
              onClick={() => {
                setIsAddTicketsModalOpen(true)
                setIsAddTicketsSuccess(false)
              }}
            >
              Ticket reservation
            </CButton>
          </CCol>
        </CRow>
      </Guard>
      <Table<Ticket>
        dataSource={tickets}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        rowKey="ticketsId"
        columns={columns}
        pagination={{
          showSizeChanger: true,
          total: totalCountOfTickets,
        }}
      />
      {isAddTicketsModalOpen && (
        <AddTicketsModal
          isVisible={isAddTicketsModalOpen}
          onClose={() => {
            setIsAddTicketsModalOpen(false)
          }}
        />
      )}
    </CCardBody>
  )
}
